// Module 10 — facility-parcel → model-node crosswalk. `facility_parcels.json` (substation/plant footprints)
// is keyed by STALE pre-V4 ids (`SUB_<osm>` / `PLANT_<osm>`), but the V4 model renamed those buses (`W<osm>`)
// and split each station into per-voltage winding nodes (`_LV###`). Without a crosswalk, clicking a
// substation footprint selects a phantom id ⇒ the inspector shows nothing (the user's "clicking a substation
// shows nothing"). This maps each parcel to the REAL model node: exact id → OSM-core base bus → nearest
// station within range. Pure (a function of the node list); Boundary: same-module only.

export interface CrosswalkNode {
  id: string;
  cls: string; // NodeClass as a string — we only special-case the station kinds
  lon: number;
  lat: number;
}

// The OSM id is the long digit run; strip a `_LV###`/`_HV###`/`_MV###` winding suffix first so a split
// winding node (W43935204_LV230 → "43935204230") doesn't pollute the core with the voltage level.
function osmCore(s: string): string {
  const m = /(\d{4,})/.exec(s.replace(/_(LV|HV|MV)\d.*$/i, ""));
  return m ? m[1]! : "";
}

const NEAR_DEG2 = 1.5e-4; // ~1.2 km² guard for the nearest-station fallback (lon/lat degrees, squared)

export function makeParcelResolver(
  nodes: readonly CrosswalkNode[],
): (parcelId: string, ring: readonly { lon: number; lat: number }[]) => string {
  const idSet = new Set(nodes.map((n) => n.id));
  // OSM-core → the BASE station bus (shortest id with that core ⇒ the un-suffixed bus, not an _LV winding).
  const baseByCore = new Map<string, string>();
  for (const n of nodes) {
    const core = osmCore(n.id);
    if (!core) continue;
    const prev = baseByCore.get(core);
    if (prev === undefined || n.id.length < prev.length) baseByCore.set(core, n.id);
  }
  const stations = nodes.filter((n) => n.cls === "substation" || n.cls === "transformer" || n.cls === "generator");

  return (parcelId, ring) => {
    if (idSet.has(parcelId)) return parcelId; // already a real node
    const base = baseByCore.get(osmCore(parcelId)); // SUB_32963410 → W32963410
    if (base) return base;
    if (ring.length === 0) return parcelId;
    // last resort: the nearest station node to the footprint centroid (orphan footprints whose bus was
    // merged/renamed without a shared OSM id) — guarded by distance so we never map to a far-away station.
    let cx = 0, cy = 0;
    for (const c of ring) { cx += c.lon; cy += c.lat; }
    cx /= ring.length; cy /= ring.length;
    let best = parcelId, bestD = Infinity;
    for (const n of stations) {
      const d = (n.lon - cx) ** 2 + (n.lat - cy) ** 2;
      if (d < bestD) { bestD = d; best = n.id; }
    }
    return bestD < NEAR_DEG2 ? best : parcelId;
  };
}
